import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subscription } from 'rxjs';
import { SupplierService } from '../../supplier.service';
import { SupplierApiKeyService } from '../../supplier-api-key.service';
import { SupplierApiTesterService } from '../../supplier-api-tester.service';
import { SupplierApiTesterClient } from '../../supplier-api-tester.client';

interface KeyIdentity {
  tenant: string;
  supplier: string;
  matchesSupplier: boolean;
}

/**
 * Onglet de test de l'API fournisseur (`/supplier/v1`), réservé aux comptes Predik hors production.
 * Parcours guidé dans les expressions de besoin et les commandes, avec le journal des appels à côté.
 * Les appels passent par les vraies routes : contrôle de la clé, limite de débit et format de réponse inclus.
 */
@Component({
  selector: 'vex-supplier-details-api-tester',
  templateUrl: './supplier-details-api-tester.component.html',
  styleUrls: ['./api-tester-shared.scss'],
})
export class SupplierDetailsApiTesterComponent implements OnInit, OnDestroy {
  section: 'tickets' | 'orders' = 'tickets';

  supplierId: string;
  supplierLabel: string;

  showKey = false;
  generatingKey = false;
  checkingKey = false;
  keyIdentity: KeyIdentity | null = null;
  keyError: string | null = null;

  private readonly subscription = new Subscription();

  constructor(
    private readonly supplierService: SupplierService,
    private readonly supplierApiKeyService: SupplierApiKeyService,
    private readonly supplierApiTesterService: SupplierApiTesterService,
    private readonly client: SupplierApiTesterClient,
    private readonly snackBar: MatSnackBar,
  ) {}

  get apiKey(): string {
    return this.supplierApiTesterService.apiKey;
  }

  ngOnInit(): void {
    this.subscription.add(
      this.supplierService.entity$.subscribe(supplier => {
        if (!supplier?._id || supplier._id === this.supplierId) return;
        this.supplierId = supplier._id;
        this.supplierLabel = supplier.label;
        this.supplierApiTesterService.useSupplier(supplier._id);
        this.keyIdentity = null;
        this.keyError = null;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onApiKeyChange(apiKey: string): void {
    this.supplierApiTesterService.setApiKey(apiKey);
    this.keyIdentity = null;
    this.keyError = null;
  }

  generateKey(): void {
    this.generatingKey = true;
    const label = `Clé de test Predik ${new Date().toLocaleString('fr-FR')}`;
    this.supplierApiKeyService.generateApiKeyValue(this.supplierId, { label }).subscribe({
      next: apiKey => {
        this.generatingKey = false;
        this.onApiKeyChange(apiKey);
        this.snackBar.open(`Clé « ${label} » générée`, 'Fermer', { duration: 3000 });
        this.checkKey();
      },
      error: () => {
        this.generatingKey = false;
        this.snackBar.open('Erreur lors de la génération de la clé', 'Fermer', { duration: 3000 });
      },
    });
  }

  checkKey(): void {
    this.checkingKey = true;
    this.client.me().subscribe(call => {
      this.checkingKey = false;
      if (call.ok && call.data?.supplier) {
        this.keyIdentity = {
          tenant: call.data.tenant?.label,
          supplier: call.data.supplier.label,
          matchesSupplier: `${call.data.supplier._id}` === this.supplierId,
        };
        this.keyError = null;
        return;
      }
      this.keyIdentity = null;
      this.keyError =
        call.status === 401
          ? 'Clé refusée : invalide, révoquée ou supprimée.'
          : `Vérification impossible : ${call.errorMessage}`;
    });
  }
}
